import { Input } from '@teamsnap/snap-ui';
import { Order } from 'components/pages/order-details';
import { PRE_FORM_REVIEWED, useAmplitude } from 'hooks/amplitude';
import * as React from 'react';
import { useEffect, useState } from 'react';
import { Adjustment } from './adjust-order-drawer';

interface Props {
  order?: Order;
  adjustment: Adjustment;
  setAdjustment: (adjustment: Adjustment) => void;
  fetchPreview: (newAmount: string) => void;
}

const useDebouncedValue = (inputValue, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(inputValue);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(inputValue);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [inputValue, delay]);

  return debouncedValue;
};

function validateAmount(
  amount: number,
  order: Order,
  adjustmentType: Partial<Adjustment>['type']
) {
  switch (adjustmentType) {
    case 'payment_schedule': {
      return true;
    }
    case 'add': {
      if (amount >= 1000000) {
        return false;
      }
      break;
    }
    case 'reduce':
    default: {
      if (order?.amountRemaining / 100 - amount < 0 || amount < 0) {
        return false;
      }
    }
  }

  return true;
}

export function AdjustOrderForm({
  order,
  adjustment,
  setAdjustment,
  fetchPreview,
}: Props) {
  const [error, setError] = useState<string>('');
  const { amount, type: adjustmentType, note } = adjustment;
  const amountFloat = parseFloat(amount);
  const { amplitudeTrack } = useAmplitude();

  const debouncedAmount = useDebouncedValue(amount, 500);

  useEffect(() => {
    if (validateAmount(debouncedAmount, order, adjustmentType)) {
      fetchPreview(debouncedAmount);
      switch (adjustmentType) {
        case 'add':
          amplitudeTrack.adjustOrderBalanceChanged(
            PRE_FORM_REVIEWED.BALANCE_INCREASE
          );
          break;
        case 'reduce':
          amplitudeTrack.adjustOrderBalanceChanged(
            PRE_FORM_REVIEWED.BALANCE_DECREASE
          );
          break;
        default:
          amplitudeTrack.adjustOrderBalanceChanged(
            PRE_FORM_REVIEWED.NO_BALANCE_CHANGE
          );
      }
    }
  }, [debouncedAmount, adjustmentType]);

  useEffect(() => {
    if (!validateAmount(amountFloat, order, adjustmentType)) {
      setError(
        adjustmentType === 'add'
          ? 'Amount entered is too large'
          : 'Amount cannot be greater than the current balance.'
      );
    } else {
      setError('');
    }
  }, [amountFloat, order, adjustmentType]);

  const isPaymentSchedule = adjustmentType === 'payment_schedule';

  return (
    <div className="sui-flex sui-gap-3">
      <div
        className={`sui-flex sui-gap-1 ${isPaymentSchedule && 'sui-w-[200px]'}`}
      >
        <Input
          name="adjustment_type"
          type={'select'}
          label="Adjustment"
          value={adjustmentType ?? 'reduce'}
          onChange={(e) => {
            setAdjustment({ ...adjustment, type: e.target.value });
            amplitudeTrack.adjustOrderBalanceChanged(
              PRE_FORM_REVIEWED[
                e.target.value === 'add'
                  ? 'BALANCE_INCREASE'
                  : e.target.value === 'reduce'
                  ? 'BALANCE_DECREASE'
                  : 'NO_BALANCE_CHANGE'
              ]
            );
          }}
          required
          options={[
            { label: 'Reduce', value: 'reduce' },
            { label: 'Add', value: 'add' },
            { label: 'Payment schedule', value: 'payment_schedule' },
          ]}
        />
        {!isPaymentSchedule && (
          <Input
            name="amount"
            allowDecimals
            label="Amount"
            onChange={(e) => {
              const newAmount = e.target.value;
              setAdjustment({
                ...adjustment,
                amount: Number.isNaN(newAmount) ? null : newAmount,
              });
            }}
            value={amount ?? ''}
            type="financial"
            errors={error ? [error] : undefined}
            placeholder="0.00"
          />
        )}
      </div>
      {!isPaymentSchedule && (
        <Input
          type="text"
          className="sui-w-3/5"
          value={note ?? ''}
          onChange={(e) =>
            setAdjustment({ ...adjustment, note: e.target.value })
          }
          name="notes"
          label="Notes"
          placeholder="e.g. additional player jersey"
        />
      )}
    </div>
  );
}
